import React, { useEffect,useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { LoadingIndicator } from "components/general/LoadingIndicator"
import {ModalConfirmAction} from "components/modals/ModalConfirmAction"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { AdminPlayerBonus2, FreeSpinsSummary } from "services/bonus/type"

import { AwardSpinsModal } from "./AwardSpinsModal"
import { ExtendSpinsModal } from "./ExtendSpinsModal"
import { FreeSpinsDetailModal } from "./FreeSpinsDetailModal"
import { FreeSpinsSummaryCards } from "./FreeSpinsSummaryCards"
import { FreeSpinsTable } from "./FreeSpinsTable"

interface FreeSpinsManagerProps {
  tenant: string
}

type ModalState =
  | { type: "details"; target: AdminPlayerBonus2 }
  | { type: "award"; target: AdminPlayerBonus2 }
  | { type: "extend"; target: AdminPlayerBonus2 }
  | { type: "cancel"; target: AdminPlayerBonus2 }
  | null

export const FreeSpinsManager: React.FC<FreeSpinsManagerProps> = ({ tenant }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [activeFreeSpins, setActiveFreeSpins] = useState<AdminPlayerBonus2[]>([])
  const [summary, setSummary] = useState<FreeSpinsSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [modal, setModal] = useState<ModalState>(null)

  useEffect(() => {
    loadData()
  }, [tenant])

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await AdminBonus2Service.getActiveFreeSpinsWithSummary(tenant)
      if (response) {
        setActiveFreeSpins(response.activeFreeSpins || [])
        setSummary(response.summary || null)
      }
    } catch (error) {
      toast.error(t("bonus2.spins.toastLoadFailed") as string)
    }
    setLoading(false)
  }

  const handleAwardSpins = async (spinsCount: number, expirationDays: number) => {
    if (modal?.type !== "award") return

    const result = await AdminBonus2Service.awardFreeSpins(modal.target.id, spinsCount, expirationDays, tenant)

    if (result) {
      toast.success(t("bonus2.spins.toastAwarded", { count: spinsCount }) as string)
      setModal(null)
      loadData()
    } else {
      toast.error(t("bonus2.spins.toastAwardFailed") as string)
    }
  }

  const handleExtendExpiration = async (additionalDays: number) => {
    if (modal?.type !== "extend") return

    const success = await AdminBonus2Service.extendFreeSpins(modal.target.id, additionalDays, tenant)

    if (success) {
      toast.success(t("bonus2.spins.toastExtended", { count: additionalDays }) as string)
      setModal(null)
      loadData()
    } else {
      toast.error(t("bonus2.spins.toastExtendFailed") as string)
    }
  }

  const handleCancelSpins = async (target: AdminPlayerBonus2) => {
    const success = await AdminBonus2Service.cancelFreeSpins(target.id, tenant)

    if (success) {
      toast.success(t("bonus2.spins.toastCancelled") as string)
      setModal(null)
      loadData()
    } else {
      toast.error(t("bonus2.spins.toastCancelFailed") as string)
    }
  }

  if (loading) {
    return <LoadingIndicator text={t("bonus2.spins.loading") as string} />
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold dark:text-white">{t("bonus2.spins.freeSpinsManagement")}</h3>
      </div>

      {summary && <FreeSpinsSummaryCards summary={summary} />}

      <FreeSpinsTable
        freeSpins={activeFreeSpins}
        onDetails={(pb) => setModal({ type: "details", target: pb })}
        onAward={(pb) => setModal({ type: "award", target: pb })}
        onExtend={(pb) => setModal({ type: "extend", target: pb })}
        onCancel={(pb) => setModal({ type: "cancel", target: pb })}
      />

      {modal?.type === "details" && <FreeSpinsDetailModal target={modal.target} onClose={() => setModal(null)} />}
      {modal?.type === "award" && <AwardSpinsModal target={modal.target} onClose={() => setModal(null)} onAward={handleAwardSpins} />}
      {modal?.type === "extend" && (
        <ExtendSpinsModal target={modal.target} onClose={() => setModal(null)} onExtend={handleExtendExpiration} />
      )}
      {modal?.type === "cancel" && (
        <ModalConfirmAction
          data={modal.target}
          handler={() => setModal(null)}
          text={t("bonus2.spins.confirmCancel", { count: modal.target.free_spins_remaining }) as string}
          actionText={t("bonus2.spins.cancelSpins") as string}
          actionHandler={handleCancelSpins}
        />
      )}
    </div>
  )
}
